import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, PieChart, Pie, Cell, ComposedChart, Bar, LabelList
} from 'recharts';
import { ArrowLeft, RefreshCw, ListChecks, Cpu, Target, Check, Star } from 'lucide-react';
import { getSmokeSanityDashboard } from '../services/api';

// Every Smoke/Sanity case falls in exactly one of these (no status counts as Not Automated).
export const SEGMENTS = [
  { key: 'automated', label: 'Automated', color: '#2d8a4e' },
  { key: 'automation_candidate', label: 'Automation Candidate', color: '#d4a017' },
  { key: 'non_automatable', label: 'Non Automatable', color: '#8a8a8a' },
  { key: 'not_automated', label: 'Not Automated', color: '#c0392b' }
];
const BUG_PRIORITY_COLORS = { High: '#c0392b', Medium: '#d4a017', Low: '#2d8a4e', Unset: '#8a8a8a', Blocker: '#1a1a2e' };
const STATUS_COLORS = { 'On Track': '#2d8a4e', 'At Risk': '#d4a017', Behind: '#c0392b' };
const MS_COLORS = { good: '#2d8a4e', warn: '#d4a017', bad: '#c0392b' };

function formatDate(d) {
  return new Date(`${d}T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}
function monthLabel(d) {
  return new Date(`${d}T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function Ring({ pct, color }) {
  const r = 38, circ = 2 * Math.PI * r;
  return (
    <svg width="92" height="92" viewBox="0 0 92 92" className="ss-ring" aria-hidden="true">
      <circle cx="46" cy="46" r={r} fill="none" stroke="var(--border)" strokeWidth="9" />
      <circle cx="46" cy="46" r={r} fill="none" stroke={color} strokeWidth="9" strokeLinecap="round"
        strokeDasharray={circ} strokeDashoffset={circ - (Math.min(pct, 100) / 100) * circ} transform="rotate(-90 46 46)" />
      <text x="46" y="52" textAnchor="middle" className="ss-ring-text">{Math.round(pct)}%</text>
    </svg>
  );
}

function Kpi({ visual, title, sub, children }) {
  return (
    <div className="ss-kpi">
      <div className="ss-kpi-visual">{visual}</div>
      <div className="ss-kpi-body">
        <span className="ss-kpi-title">{title}</span>
        {sub && <span className="ss-kpi-sub">{sub}</span>}
        {children}
      </div>
    </div>
  );
}

export function ProgressRow({ row }) {
  return (
    <div className="ss-row">
      <div className="ss-row-label"><strong>{row.type}</strong><span>({row.total})</span></div>
      <div className="ss-bar" role="img" aria-label={`${row.type} automation progress`}>
        {SEGMENTS.map(s => row[s.key] > 0 && (
          <div key={s.key} className="ss-seg" title={`${s.label}: ${row[s.key]}`}
            style={{ flexGrow: row[s.key], background: s.color }}>{row[s.key]}</div>
        ))}
      </div>
      <div className="ss-row-pct">
        {Math.round(row.coverage_pct)}%
        {row.status && <span className="ss-badge" style={{ background: STATUS_COLORS[row.status] }}>{row.status}</span>}
      </div>
    </div>
  );
}

export function AutomationBugs({ bugs }) {
  if (!bugs) return <p className="auto-empty-text">Bug data is unavailable right now.</p>;
  if (!bugs.total) return <p className="auto-empty-text">No automation bugs detected yet.</p>;
  return (
    <div className="ss-bugs">
      <div className="ss-bugs-chart">
        <ResponsiveContainer width="100%" height={230}>
          <PieChart>
            <Pie data={bugs.byPriority} dataKey="count" nameKey="priority" innerRadius={62} outerRadius={95}
              isAnimationActive={false} label={({ value }) => value} labelLine={false}>
              {bugs.byPriority.map(b => <Cell key={b.priority} fill={BUG_PRIORITY_COLORS[b.priority] || '#8a8a8a'} />)}
            </Pie>
            <Tooltip />
            <Legend verticalAlign="bottom" />
          </PieChart>
        </ResponsiveContainer>
        <div className="ss-bugs-total"><strong>{bugs.total}</strong><span>Detected</span></div>
      </div>
      <div className="ss-bugs-side">
        <div><strong>{bugs.open}</strong><span>Open</span></div>
        <div><strong>{bugs.closed}</strong><span>Closed</span></div>
      </div>
    </div>
  );
}

/** Plan checkpoints worth showing: the first point, then every third point, and the target (last point). */
function isMilestonePoint(i, plan) {
  return i === 1 || (i >= 3 && i % 3 === 0) || i === plan.length - 1;
}

/**
 * Baseline (first month with automation), plan checkpoints and the target. A checkpoint whose month has ended is
 * reached (green) when coverage got there, else missed (red); future ones take the
 * colour of the overall on-track status (green on track, amber at risk, red behind).
 */
export function buildMilestones(summary, trend, status) {
  const bestActual = Math.max(0, ...trend.map(t => t.actual ?? 0));
  const futureTone = { 'On Track': 'good', 'At Risk': 'warn', Behind: 'bad' }[status] || 'good';
  // The baseline is dated at the first month with automated cases in TestRail history.
  const baselineDate = summary.first_automation_date || (summary.trend_backfill || [])[0]?.date || summary.date;
  const out = [{ date: baselineDate, text: 'Baseline established', state: 'reached', tone: 'good' }];
  (summary.plan || []).forEach((p, i, plan) => {
    const isTarget = i === plan.length - 1;
    if (!isMilestonePoint(i, plan)) return;
    const reached = bestActual >= p.pct;
    const due = p.date <= summary.date;
    const state = reached ? 'reached' : due ? 'missed' : 'upcoming';
    const detail = `${p.pct}% coverage${p.automated != null ? ` (${p.automated.toLocaleString()} TCs)` : ''}`;
    out.push({
      date: p.date, target: isTarget, state,
      text: isTarget ? `Target: ${detail}` : `Reach ${detail}`,
      tone: state === 'reached' ? 'good' : state === 'missed' ? 'bad' : futureTone
    });
  });
  return out;
}

function Milestones({ items }) {
  return (
    <ol className="ss-milestones">
      {items.map(m => (
        <li key={m.date} className={`ss-ms-${m.state}`}>
          <span className="ss-ms-dot" style={{ borderColor: MS_COLORS[m.tone], background: m.state === 'reached' ? MS_COLORS[m.tone] : undefined }}>
            {m.target ? <Star size={16} fill={MS_COLORS[m.tone]} color={MS_COLORS[m.tone]} /> : m.state === 'reached' ? <Check size={12} /> : null}
          </span>
          <strong>{monthLabel(m.date)}</strong>
          <span style={m.state === 'upcoming' ? undefined : { color: MS_COLORS[m.tone] }}>{m.text}</span>
        </li>
      ))}
    </ol>
  );
}

/** Monthly newly-automated cases (bars) and cumulative automated (line) on one axis, from case history. */
export function buildExecutionTrend(backfill) {
  const fmt = (date, opts) => new Date(`${date}T00:00:00Z`).toLocaleDateString(undefined, { ...opts, timeZone: 'UTC' });
  return backfill.map((p, i) => {
    const next = new Date(`${p.date}T00:00:00Z`);
    next.setUTCDate(next.getUTCDate() + 1);
    const partial = next.getUTCDate() !== 1; // snapshot date is not a month end
    return {
      month: `${fmt(p.date, { month: 'short' })} \u2019${fmt(p.date, { year: '2-digit' })}${partial ? ' (to date)' : ''}`,
      // new_automated is exact even when the window starts mid-history; older snapshots lack it
      monthly: p.new_automated ?? p.automated - (i ? backfill[i - 1].automated : 0),
      cumulative: p.automated
    };
  });
}

function SmokeSanityDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getSmokeSanityDashboard()
      .then(setData)
      .catch(err => setError(err.message || 'Failed to load dashboard data'))
      .finally(() => setLoading(false));
  }, []);

  const s = data?.summary;
  const total = data?.rows?.find(r => r.type === 'Total');
  const rows = data?.rows || [];
  const backfill = s?.trend_backfill || [];
  const prev = backfill.length > 1 ? backfill[backfill.length - 2] : null;
  const delta = prev && prev.automated ? Math.round((total.automated - prev.automated) * 100 / prev.automated) : null;

  return (
    <div className="auto-page">
      <div className="auto-header">
        <div className="auto-header-left">
          <button className="auto-back-btn" onClick={() => navigate('/dashboards')}><ArrowLeft size={16} /></button>
          <div>
            <h1 className="auto-title">Smoke + Sanity Automation</h1>
            <p className="auto-subtitle">
              {s ? `Path to ${s.target_pct}% automation coverage by ${formatDate(s.target_date)}` : 'Smoke + Sanity automation coverage'}
            </p>
          </div>
        </div>
        {s && <div className="auto-header-right"><span className="tr-panel-sub">Last updated {formatDate(s.date)}</span></div>}
      </div>

      {loading ? (
        <div className="tr-loading"><RefreshCw size={20} className="spin" /> Loading...</div>
      ) : error ? (
        <div className="tr-nodata"><h3>Error loading data</h3><p>{error}</p></div>
      ) : !data?.available ? (
        <div className="tr-nodata"><h3>No data available</h3><p>The first snapshot has not been generated yet.</p></div>
      ) : (
        <div className="ss-layout">
          <section className="ss-kpis">
            <Kpi visual={<Ring pct={total.coverage_pct} color="#2d8a4e" />} title="Current Coverage"
              sub={`${total.automated} / ${total.total} automated`} />
            <Kpi visual={<Ring pct={s.target_pct} color="var(--primary)" />} title="Target Coverage"
              sub={`By ${formatDate(s.target_date)}`} />
            <Kpi visual={<ListChecks size={34} />} title="Current In-scope Library" sub="Smoke + Sanity">
              <span className="ss-kpi-big">{total.total}</span>
              <span className="ss-kpi-sub">Test cases</span>
            </Kpi>
            <Kpi visual={<Cpu size={34} />} title="Automated Cases" sub="Smoke + Sanity">
              <span className="ss-kpi-big">{total.automated}</span>
              {delta != null && <span className="ss-kpi-sub">{delta >= 0 ? '▲' : '▼'} {Math.abs(delta)}% vs previous month</span>}
            </Kpi>
            <Kpi visual={<Target size={34} />} title="Overall Status" sub="Smoke + Sanity">
              <span className="ss-kpi-status" style={{ color: STATUS_COLORS[data.status] }}>● {data.status}</span>
              <span className="ss-kpi-sub">
                {data.plannedPct == null ? '' : `Plan today ${data.plannedPct}%`}
                {data.plannedPct != null && data.daysLeft != null ? ' · ' : ''}
                {data.daysLeft != null && `${data.daysLeft} days to target`}
              </span>
            </Kpi>
          </section>

          <div className="ss-grid-2">
            <section className="tr-panel">
              <div className="tr-panel-head"><h3>Smoke + Sanity Coverage Trend</h3></div>
              <div className="tr-panel-body">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={data.trend} margin={{ left: 0, right: 20, top: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={d => d.slice(0, 7)} minTickGap={24} />
                    <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={v => (v == null ? '-' : `${v}%`)} />
                    <Legend />
                    <ReferenceLine y={s.target_pct} stroke="#c0392b" strokeDasharray="6 4" label={{ value: `Target ${s.target_pct}%`, position: 'insideTopLeft', fill: '#c0392b', fontSize: 12 }} />
                    <Line type="monotone" isAnimationActive={false} dataKey="planned" name="Planned Coverage" stroke="#8a8a8a" strokeDasharray="5 5" dot={false} />
                    <Line type="monotone" isAnimationActive={false} dataKey="actual" name="Actual Coverage" stroke="#2d8a4e" strokeWidth={2} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </section>

            <section className="tr-panel">
              <div className="tr-panel-head"><h3>Smoke + Sanity Automation Progress</h3></div>
              <div className="tr-panel-body">
                <div className="ss-legend">
                  {SEGMENTS.map(seg => (
                    <span key={seg.key}><i style={{ background: seg.color }} />{seg.label}</span>
                  ))}
                </div>
                {rows.map(r => <ProgressRow key={r.type} row={r} />)}
              </div>
            </section>
          </div>

          <div className="ss-grid-3">
            <section className="tr-panel">
              <div className="tr-panel-head">
                <h3>Automation Bugs Found</h3>
                <span className="tr-panel-sub">All automation-found bugs (not limited to Smoke/Sanity)</span>
              </div>
              <div className="tr-panel-body"><AutomationBugs bugs={data.automationBugs} /></div>
            </section>

            <section className="tr-panel">
              <div className="tr-panel-head"><h3>Key Milestones</h3></div>
              <div className="tr-panel-body"><Milestones items={buildMilestones(s, data.trend, data.status)} /></div>
            </section>

            <section className="tr-panel">
              <div className="tr-panel-head"><h3>Automation Execution Trend</h3></div>
              <div className="tr-panel-body">
                {backfill.length ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <ComposedChart data={buildExecutionTrend(backfill)} margin={{ left: 0, right: 0, top: 18 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false}
                        label={{ value: 'Automated test cases', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#555', textAnchor: 'middle' } }} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="monthly" name="Newly automated in month (bars)" fill="#2d8a4e" isAnimationActive={false}>
                        <LabelList dataKey="monthly" position="insideTop" style={{ fontSize: 11, fontWeight: 600, fill: '#fff' }}
                          formatter={v => (v >= 12 ? v : '')} />
                      </Bar>
                      <Line type="monotone" dataKey="cumulative" name="Cumulative automated (line)" stroke="#3f9b67" strokeWidth={2} isAnimationActive={false}>
                        <LabelList dataKey="cumulative" position="top" style={{ fontSize: 11, fontWeight: 600, fill: '#3f9b67' }} />
                      </Line>
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : <p className="auto-empty-text">No automation history yet.</p>}
              </div>
            </section>
          </div>

          <div className="tr-refresh-info">
            <span>Data from TestRail run: {s.run_name || `Run ${s.run_id}`}</span>
            <span>
              Smoke / Sanity cases only; excludes {(s.excluded_sections || []).join(', ')}
              {s.excluded_top_level?.length > 0 && ` and top-level ${s.excluded_top_level.join(', ')}`}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default SmokeSanityDashboard;
